import i18n from '@/utils/i18n';
import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  DatePickerCalendar,
  Typography,
  XStack,
  YStack,
} from '@usepepper/sprout-kit-ui';
import { useTheme } from '@/contexts/theme-context';

interface OverrideExpirationPickerProps {
  canAdjust: boolean;
  initialExpiryDate: Date | null;
  onApply: (date: Date) => void;
  onRemove: () => void;
  showRemove: boolean;
}

/**
 * Calendar panel for staging a temporary-adjustment expiry date.
 *
 * `canAdjust` (the field is dirty or already carries an override) gates Apply
 * and drives the helper note — an expiry can only be attached to an actual
 * adjustment. The expiry is end-of-day, so today is the minimum valid date.
 */
export const OverrideExpirationPicker: React.FC<
  OverrideExpirationPickerProps
> = ({ canAdjust, initialExpiryDate, onApply, onRemove, showRemove }) => {
  const theme = useTheme();

  const [draftDate, setDraftDate] = useState<Date | null>(initialExpiryDate);

  const startOfToday = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  // Past dates are rejected here rather than via the calendar's minDate prop:
  // a bug in DatePickerCalendar with minDate set cannot navigate more than one month
  // into the future, which would prevent users from setting expiries in the far future.
  const isDraftInPast = !!draftDate && draftDate < startOfToday;
  const isApplyDisabled = !draftDate || !canAdjust || isDraftInPast;

  const selectedDates = useMemo(
    () => (draftDate ? [draftDate] : []),
    [draftDate],
  );

  const handleChange = useCallback((dates: Date[]) => {
    setDraftDate(dates[0] ?? null);
  }, []);

  const handleApply = () => {
    if (!draftDate || isApplyDisabled) {
      return;
    }
    onApply(draftDate);
  };

  return (
    <YStack gap={theme.sk.spacing.spaceMedium}>
      <DatePickerCalendar
        mode="single"
        onChange={handleChange}
        value={selectedDates}
      />
      {!canAdjust ? (
        <Typography color="$textSecondary" size="$xs">
          {i18n.t(
            'sales_portal.order_entry.table.temporary_adjustment.helper_note',
          )}
        </Typography>
      ) : null}
      <XStack
        borderTopColor={theme.sk.color.border.primary}
        borderTopWidth={1}
        gap={theme.sk.spacing.spaceSmall}
        justifyContent="flex-end"
        paddingTop={theme.sk.spacing.spaceSmall}
      >
        {showRemove ? (
          <Button
            marginRight="auto"
            onPress={onRemove}
            size="$sm"
            variant="tertiary"
          >
            {i18n.t(
              'sales_portal.order_entry.table.temporary_adjustment.remove',
            )}
          </Button>
        ) : null}
        <Button
          disabled={isApplyDisabled}
          onPress={handleApply}
          size="$sm"
          variant="primary"
        >
          {i18n.t('sales_portal.order_entry.table.temporary_adjustment.apply')}
        </Button>
      </XStack>
    </YStack>
  );
};
